import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, Input, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { faCodeBranch, faGripDotsVertical, faPlus, faStar, faThumbTack, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { QuestionOption, QuestionType } from '@empathy/survey/base';

import { SideOptionTabs } from '../../../models/side-option-tabs.enum';
import { IQuestionOptionForm } from '../../../models/type-forms/question-form-group.interface';
import { QuestionOptionForm } from '../../../models/type-forms/question-option-form.model';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { TextEditorComponent } from '../../text-editor/text-editor.component';
import { GetNextId } from '@eiq/shared/utils';

@Component({
  selector: 'empathy-hidden-choices-form',
  templateUrl: 'hidden-choices-form.component.html',
  styleUrls: ['./hidden-choices-form.component.scss', './_hidden-choices-form-theme.component.scss']
})
export class HiddenChoicesFormComponent {
  @ViewChildren(TextEditorComponent) ckEditor: TextEditorComponent[];

  @Input() allowOther = true;

  faCodeBranch = faCodeBranch;
  faGripDotsVertical = faGripDotsVertical;
  faPlus = faPlus;
  faStar = faStar;
  faStarSolid = faStarSolid;
  faThumbTack = faThumbTack;
  faTrashCan = faTrashCan;
  justAddedOptionId: string;
  questionType = QuestionType;

  constructor(
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    public surveyEditorService: SurveyEditorService
  ) {}

  get options(): FormArray {
    return this.surveyEditorService.surveyEditor$().activeHiddenQuestionOptions;
  }

  get addOptionAllowed(): boolean {
    return this.options.controls.length !== 0 && this.options.controls[this.options.controls.length - 1].value.text?.length > 0;
  }

  onPaste(event: string[], pastedOnIndex: number): void {
    const currentOption = this.options.at(pastedOnIndex);
    const isCurrentEmpty = !currentOption.get('text').value;

    event.forEach((text, index) => {
      if (index === 0 && isCurrentEmpty) {
        currentOption.get('text').setValue(text);
      } else {
        const insertIndex = pastedOnIndex + index + (isCurrentEmpty ? 0 : 1);
        const { id, value } = GetNextId(this.options.value, 'opt');
        const newOption = new QuestionOptionForm({ id, value, text } as QuestionOption);
        this.options.insert(insertIndex, newOption.form);
      }
    });

    this.updateOptionIds();
    this.cdr.detectChanges();
  }

  private updateOptionIds(): void {
    this.options.controls.forEach((control, index) => {
      const newValue = `${index + 1}`;
      const newId = `opt_${newValue}`;
      control.get('id').setValue(newId);
      control.get('value').setValue(newValue);
    });
  }

  onAddOption(isLastOption: boolean, index: number = null): void {
    if (!isLastOption) {
      const option = this.options.value[index + 1];
      const editor: DecoupledEditor = this.ckEditor.find((ed) => ed.id === option.id).editorInstance;
      editor.editing.view.focus();
      return;
    }

    if (!this.addOptionAllowed) {
      return;
    }

    if (this.options.controls.length === 0 || this.options.controls[this.options.controls.length - 1].value.text?.length > 0) {
      const newOption = new QuestionOptionForm();
      const newOptionId = GetNextId(this.options.value, 'opt');
      newOption.form.get('id').setValue(newOptionId.id);
      newOption.form.get('value').setValue(newOptionId.value);
      this.justAddedOptionId = newOptionId.id;
      this.options.push(newOption.form);

      this.cdr.detectChanges();
    }
  }

  onOpenConditionals(option: AbstractControl): void {
    this.surveyEditorService.surveyEditor$().activeHiddenOption = option as FormGroup<IQuestionOptionForm>;
    this.surveyEditorService.activeTab$.set(SideOptionTabs.Conditions);
  }

  onRemoveAt(index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Hidden Variable Option',
        confirmText: 'Are you sure you want to remove this hidden variable condition?',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.options.removeAt(index);
      }
    });
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.options.controls, event.previousIndex, event.currentIndex);
  }
}
